import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { useMentorSession } from '../context/TrackerContext';
import { getRecentClasses, updateClassStatus } from '../lib/api';
import type { RecentClassRow } from '../lib/api';
import { ClassLogEditModal, EditRequestChip } from '../components/ClassLogEdit';

type CompletionStatus = 'Task Given' | 'Completed' | 'Not Completed';
type StatusFilter = 'All' | CompletionStatus;

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  'Task Given':    { bg: '#fef9c3', color: '#854d0e' },
  'Completed':     { bg: '#dcfce7', color: '#15803d' },
  'Not Completed': { bg: '#fee2e2', color: '#991b1b' },
  'N/A':           { bg: '#f3f4f6', color: '#9ca3af' },
};

const MentorTaskStatus: React.FC = () => {
  const { mentorId } = useMentorSession();
  const [rows, setRows]         = useState<RecentClassRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState<string | null>(null);
  const [filterStudent, setFilterStudent] = useState('all');
  const [filterStatus, setFilterStatus]   = useState<StatusFilter>('Task Given');
  const [taskInputs, setTaskInputs] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<RecentClassRow | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    return getRecentClasses(mentorId, 100)
      .then(res => {
        setRows(res.rows);
        const init: Record<string, string> = {};
        res.rows.forEach(r => {
          init[r.logRowIndex] = r.tasksCompleted != null && r.tasksCompleted > 0
            ? String(r.tasksCompleted) : '';
        });
        setTaskInputs(init);
      })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [mentorId]);

  useEffect(() => { load(); }, [load]);

  const markStatus = async (row: RecentClassRow, newStatus: CompletionStatus) => {
    if (saving === row.logRowIndex) return;
    setSaving(row.logRowIndex);
    const tasksCompleted = taskInputs[row.logRowIndex] !== '' && taskInputs[row.logRowIndex] !== undefined
      ? Number(taskInputs[row.logRowIndex]) : undefined;
    try {
      await updateClassStatus(mentorId, row.logRowIndex, row.studentId, row.date, newStatus, tasksCompleted);
      toast.success(`${row.studentName} — marked ${newStatus}`);
      // Reload from server to confirm persistence
      await load();
    } catch (err: any) {
      toast.error('Update failed: ' + err.message);
      setSaving(null);
    }
  };

  // Unique students from ALL rows (not filtered)
  const students = Array.from(new Map(rows.map(r => [r.studentId, r.studentName])).entries());

  // Apply both filters; "Task Given" excludes absent students (they show under "All")
  const filtered = rows
    .filter(r => filterStudent === 'all' || r.studentId === filterStudent)
    .filter(r => {
      if (filterStatus === 'All') return true;
      if (filterStatus === 'Task Given') return r.completionStatus === 'Task Given' && r.attendance !== 'Absent';
      return r.completionStatus === filterStatus;
    });

  // N/A = absent students, they have no tasks — exclude from pending count
  const pendingCount = rows.filter(r => r.completionStatus === 'Task Given' && r.attendance !== 'Absent').length;

  return (
    <TrackerLayout>
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Task Status</h1>
            <p className="text-sm text-gray-500 mt-1">Update task completion for your recent classes</p>
          </div>
          {pendingCount > 0 && (
            <span className="text-xs font-bold px-4 py-2 rounded-full" style={{ backgroundColor: '#fef9c3', color: '#854d0e' }}>
              {pendingCount} task{pendingCount !== 1 ? 's' : ''} pending update
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="flex items-center gap-2 flex-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Student</label>
            <select value={filterStudent} onChange={e => setFilterStudent(e.target.value)}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400">
              <option value="all">All Students</option>
              {students.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Status</label>
            <div className="flex gap-1 flex-wrap">
              {(['Task Given', 'Completed', 'Not Completed', 'All'] as StatusFilter[]).map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors"
                  style={filterStatus === s
                    ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                    : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button onClick={load} disabled={loading}
            className="text-xs font-semibold px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap shrink-0">
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 py-20 text-center text-gray-400 text-sm">
            Loading classes…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
            <p className="text-gray-500 font-medium text-sm">
              {filterStatus === 'Task Given' ? 'No pending tasks — all up to date!' : 'No classes found for this filter.'}
            </p>
            <button onClick={() => setFilterStatus('All')} className="text-xs text-blue-500 hover:underline mt-2">
              Show all classes
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(row => {
              const st        = STATUS_STYLE[row.completionStatus] || STATUS_STYLE['Task Given'];
              const isSaving  = saving === row.logRowIndex;
              const isPresent = row.attendance === 'Present';
              const isPending = row.completionStatus === 'Task Given';

              return (
                <div key={row.logRowIndex}
                  className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm transition-all"
                  style={isPending ? { borderLeftWidth: 4, borderLeftColor: '#F0B429' } : {}}>

                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-sm" style={{ color: '#2b2240' }}>{row.studentName}</span>
                        <span className="text-xs text-gray-400">{row.date}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPresent ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {row.attendance}
                        </span>
                        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                          style={{ backgroundColor: st.bg, color: st.color }}>
                          {row.completionStatus}
                        </span>
                        <button onClick={() => setEditing(row)}
                          className="ml-auto text-xs font-bold px-2.5 py-1 rounded-lg border-2 hover:bg-purple-50"
                          style={{ borderColor: '#c4b5fd', color: '#6d28d9' }}>
                          ✏️ {row.editRequest?.status === 'Pending' ? 'Revise Edit' : 'Edit'}
                        </button>
                      </div>

                      {row.topic && (
                        <div className="flex items-start gap-1.5 mt-1.5">
                          <span className="text-xs font-bold uppercase tracking-wide shrink-0 mt-0.5" style={{ color: '#F0B429' }}>Task:</span>
                          <span className="text-sm text-gray-800">{row.topic}</span>
                        </div>
                      )}
                      {row.portion && row.portion !== row.topic && (
                        <div className="flex items-start gap-1.5 mt-0.5">
                          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide shrink-0 mt-0.5">Portion:</span>
                          <span className="text-xs text-gray-500">{row.portion}</span>
                        </div>
                      )}
                      <EditRequestChip req={row.editRequest} />

                      {isPresent && (
                        <div className="flex items-center gap-3 mt-3 flex-wrap">
                          <span className="text-xs text-gray-500">
                            Tasks given: <strong style={{ color: '#2b2240' }}>{row.tasksGiven ?? 0}</strong>
                          </span>
                          <div className="flex items-center gap-1.5">
                            <label className="text-xs text-gray-500">Completed:</label>
                            <input
                              type="number"
                              min="0"
                              max={row.tasksGiven ?? 99}
                              value={taskInputs[row.logRowIndex] ?? ''}
                              onChange={e => setTaskInputs(prev => ({ ...prev, [row.logRowIndex]: e.target.value }))}
                              disabled={isSaving}
                              placeholder="0"
                              className="w-16 border-2 border-gray-200 rounded-lg px-2 py-1 text-sm font-bold text-center focus:outline-none focus:border-yellow-400"
                              style={{ color: '#2b2240' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    {isPresent && (
                      <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                        {isSaving ? (
                          <div className="flex items-center gap-2 px-3 py-2">
                            <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                            <span className="text-xs text-gray-400">Saving…</span>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => markStatus(row, 'Completed')}
                              disabled={row.completionStatus === 'Completed'}
                              className="px-4 py-2 rounded-lg text-xs font-bold border-2 transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100 whitespace-nowrap"
                              style={{ borderColor: '#16a34a', color: '#16a34a', backgroundColor: '#f0fdf4' }}>
                              Completed
                            </button>
                            <button
                              onClick={() => markStatus(row, 'Not Completed')}
                              disabled={row.completionStatus === 'Not Completed'}
                              className="px-4 py-2 rounded-lg text-xs font-bold border-2 transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100 whitespace-nowrap"
                              style={{ borderColor: '#dc2626', color: '#dc2626', backgroundColor: '#fef2f2' }}>
                              Not Completed
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && (
          <p className="text-xs text-gray-400 mt-4 text-right">
            {filtered.length} of {rows.length} classes shown
          </p>
        )}
      </div>

      {editing && (
        <ClassLogEditModal row={editing} mentorId={mentorId}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }} />
      )}
    </TrackerLayout>
  );
};

export default MentorTaskStatus;
